import { useQuery } from '@tanstack/react-query'
import { isWithinInterval } from 'date-fns'
import { z } from 'zod'

import { httpService } from '@/api/v1/http.service'
import type { Lesson } from '@/types/lesson'
import type { Semester } from '@/types/semester'
import { date, requiredString, semesterAndYearStatus } from '@/utils/zod'

const responseSchema = z.object({
  id: requiredString(),
  name: requiredString(),
  semesters: z.array(
    z.object({
      id: requiredString(),
      name: requiredString(),
      start_date: date(),
      end_date: date(),
      is_active: z.boolean(),
      status: semesterAndYearStatus
    })
  ),
  teacher_covers: z.array(z.string()),
  co_teacher_1_covers: z.array(z.string()),
  teacher: z.object({
    id: requiredString(),
    name: requiredString()
  }),
  co_teacher_1: z
    .object({
      id: requiredString(),
      name: requiredString()
    })
    .nullable()
})

export type Group = {
  id: string
  name: string
  semesters: Semester[]
  teacherCovers: string[]
  coTeacherCovers: string[]
  teacher: { id: string; fullName: string }
  coTeacher?: { id: string; fullName: string }
}

const parseResponse = (response: z.infer<typeof responseSchema>): Group => ({
  id: response.id,
  name: response.name,
  semesters: response.semesters.map(semester => ({
    id: semester.id,
    name: semester.name,
    startDate: semester.start_date,
    endDate: semester.end_date,
    isActive: semester.is_active,
    status: semester.status
  })),
  teacherCovers: response.teacher_covers,
  coTeacherCovers: response.co_teacher_1_covers,
  teacher: { id: response.teacher.id, fullName: response.teacher.name },
  coTeacher: response.co_teacher_1
    ? {
        id: response.co_teacher_1.id,
        fullName: response.co_teacher_1.name
      }
    : undefined
})

export const groupQueryOptions = (id: string) => ({
  queryKey: ['panelLessonsGroupsRetrieve', id],
  staleTime: 60 * 100,
  queryFn: async () => {
    const groupResponse = await httpService.panel.panelLessonsGroupsRetrieve(
      id,
      {
        fetchKeys: {
          id: true,
          name: true,
          semesters: true,
          teacher_covers: true,
          co_teacher_1_covers: true,
          teacher: true,
          co_teacher_1: true
        }
      }
    )

    const group = parseResponse(responseSchema.parse(groupResponse))

    const currentSemester = group.semesters?.find(semester => semester.isActive)

    return {
      ...group,
      semestersOptions: group.semesters.map(semester => ({
        label: semester.name,
        value: semester.id
      })),
      currentSemester:
        currentSemester || group.semesters[group.semesters.length - 1],
      getLessonSemester: (lesson?: Lesson) => {
        if (!group || !lesson) return

        return group.semesters.find(semester =>
          isWithinInterval(lesson.startDate, {
            start: semester.startDate,
            end: semester.endDate
          })
        )
      }
    }
  }
})

const useGroup = (id: string) => useQuery(groupQueryOptions(id))

export default useGroup
